// SideBar.jsx

import { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  AcademicCapIcon,
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  CalendarIcon,
  Cog6ToothIcon,
  FunnelIcon,
  NewspaperIcon,
  SquaresPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { NavLink, useNavigate } from "react-router-dom";
import { useGateOnUpdate } from "../hooks/useGateOnUpdate";
import SquawkPlayerButton from "./squawk-player/SquawkPlayerButton";
import EconomicCalendarCountdown from "../ui-components/EconomicCalendarCountdown";
import { useStore } from "../stores/store";

import useAuth from "../hooks/useAuth";
import { useHasInternalNewsquawkUserRole } from "../hooks/useHasInternalNewsquawkUserRole";
import { useSettingsStore } from "../stores/SettingsStore";
import * as Sentry from "@sentry/react";

// Helper function to convert text to capital case (title case)
const toCapitalCase = (text) => {
  if (!text) return "";
  return text
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const navigation = [
  { name: "Dashboard", href: "/dashboard", icon: SquaresPlusIcon },
  {
    name: "Watchlists & Filters",
    href: "/watchlists-filters",
    icon: FunnelIcon,
  },
  {
    name: "Calendar",
    href: "/calendar",
    icon: CalendarIcon,
  },
  { name: "Research Suite", href: "/research", icon: AcademicCapIcon },
  {
    name: "Legacy View",
    href: "/headlines",
    icon: NewspaperIcon,
    authOnly: true,
    requiresLegacyView: true,
  },
];

const appEnv = import.meta.env.VITE_APP_ENV;

const navigationLower = [
  { name: "Settings", href: "/settings", icon: Cog6ToothIcon },
];

const SideBar = () => {
  const { authenticated, keycloakInstance: keycloak, tokenParsed } = useAuth();

  const isInternalUser = useHasInternalNewsquawkUserRole();
  const legacyViewEnabled = useSettingsStore((state) => state.legacyViewEnabled);
  const navigate = useNavigate();
  const gate = useGateOnUpdate();

  // Get user name from Keycloak token
  const userName = tokenParsed?.name || "";

  const desktopSidebarOpen = useStore((state) => state.desktopSidebarOpen);

  const setDesktopSidebarOpen = useStore(
    (state) => state.setDesktopSidebarOpen
  );
  const mobileSidebarOpen = useStore((state) => state.mobileSidebarOpen);

  const setMobileSidebarOpen = useStore((state) => state.setMobileSidebarOpen);

  // Filter navigation items based on user role
  const filteredNavigation = navigation.filter((item) => {
    // Hidden when the user has disabled the Legacy View in Settings
    if (item.requiresLegacyView && !legacyViewEnabled) {
      return false;
    }
    // If the item is internal only, check if the user is an internal user
    if (item.internalOnly) {
      return isInternalUser;
    }
    if (item.authOnly) {
      return authenticated;
    }
    // Otherwise, include the item for all users
    return true;
  });

  const handleLogin = () => {
    keycloak.login();
  };

  const handleLogout = () => {
    keycloak.logout();
  };

  // State to track if window height is constrained
  const [isHeightConstrained, setIsHeightConstrained] = useState(
    typeof window !== "undefined" ? window.innerHeight < 900 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsHeightConstrained(window.innerHeight < 900);
    };

    window.addEventListener("resize", handleResize);

    // Initial check
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {/* Mobile Sidebar */}
      {mobileSidebarOpen && (
        <Transition.Root show={mobileSidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50 lg:hidden"
            onClose={setMobileSidebarOpen}
          >
            {/* Overlay */}
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            {/* Sidebar */}
            <div className="fixed inset-0 flex z-50">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-gray-900">
                  {/* Main content and Bottom items combined */}
                  <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto flex flex-col">
                    {/* Header: Logo and Close Button */}
                    <div className="flex items-center justify-between px-4">
                      <img
                        className="w-20"
                        src="/newsquawk-logo-white.png"
                        alt="Newsquawk Logo"
                      />
                      <button
                        type="button"
                        className="text-white bg-gray-800 hover:text-white p-2 rounded-md focus:outline-none"
                        onClick={() => setMobileSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    {/* Navigation */}
                    <nav className="mt-5 px-2">
                      <div className="space-y-1">
                        {filteredNavigation.map((item) => (
                          <NavLink
                            key={item.name}
                            to={item.href}
                            className={({ isActive }) =>
                              isActive
                                ? "bg-gray-800 text-squawk-blue hover:text-squawk-blue group flex items-center px-2 py-2 text-base font-medium rounded-md"
                                : "text-gray-400 hover:text-white hover:bg-gray-800 group flex items-center px-2 py-2 text-base font-medium rounded-md"
                            }
                            onClick={() => setMobileSidebarOpen(false)} // Close sidebar on navigation
                          >
                            <item.icon
                              className="mr-4 h-6 w-6"
                              aria-hidden="true"
                            />
                            {item.name}
                          </NavLink>
                        ))}
                      </div>
                    </nav>

                    {/* Bottom Items */}
                    <div className="mt-auto p-4 w-full">
                      {authenticated ? (
                        <>
                          {userName && (
                            <div className="text-center mb-3 px-2 py-2 text-sm text-gray-300 font-medium select-none">
                              {toCapitalCase(userName)}
                            </div>
                          )}
                          <ul className="-mx-2 space-y-1 text-center">
                            {navigationLower.map((item) => (
                              <li key={item.name}>
                                <NavLink
                                  key={item.name}
                                  to={item.href}
                                  className={({ isActive }) =>
                                    isActive
                                      ? "bg-gray-800 text-squawk-blue hover:text-squawk-blue group flex items-center px-2 py-2 text-base font-medium rounded-md"
                                      : "text-gray-400 hover:text-white hover:bg-gray-800 group flex items-center px-2 py-2 text-base font-medium rounded-md"
                                  }
                                  onClick={(e) => {
                                    e.preventDefault();
                                    gate(() => {
                                      setMobileSidebarOpen(false);
                                      navigate(item.href);
                                    })();
                                  }}
                                >
                                  <item.icon
                                    className="mr-4 h-6 w-6"
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                </NavLink>
                              </li>
                            ))}
                            <li>
                              <button
                                className="text-gray-400 bg-transparent w-full hover:text-white hover:bg-gray-800 border-none group flex items-center px-2 py-2 text-base font-medium rounded-md"
                                onClick={() => {
                                  setMobileSidebarOpen(false);
                                  handleLogout();
                                }}
                              >
                                <ArrowLeftOnRectangleIcon className="h-6 w-6 text-gray-400 group-hover:text-white" />
                                <span className="ml-3 text-base font-medium text-gray-400 group-hover:text-white">
                                  Logout
                                </span>
                              </button>
                            </li>
                          </ul>
                        </>
                      ) : (
                        <button
                          className="w-full flex items-center text-left group bg-transparent focus:outline-none"
                          onClick={() => {
                            setMobileSidebarOpen(false);
                            handleLogin();
                          }}
                        >
                          <ArrowRightOnRectangleIcon className="h-6 w-6 text-gray-400 group-hover:text-white" />
                          <span className="ml-3 text-base font-medium text-gray-400 group-hover:text-white">
                            Login
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
              <div className="flex-shrink-0 w-14" aria-hidden="true">
                {/* Force sidebar to shrink to fit close icon */}
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      )}

      {/* Desktop Sidebar */}
      {desktopSidebarOpen && (
        <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-20 lg:flex-col bg-gray-900 relative before:absolute before:right-0 before:top-12 before:bottom-0 before:w-px before:bg-gray-700/50">
          <div className="flex flex-col h-screen">
            {/* Logo */}
            <div
              className="flex h-12 items-center justify-center cursor-pointer bg-gray-900"
              onClick={() => setDesktopSidebarOpen(false)}
            >
              <img
                className="w-16"
                src="/newsquawk-logo-white.png"
                alt="Newsquawk Logo"
              />
            </div>

            {/* Staging Website Component */}
            <div className="h-auto mx-auto px-1 text-center items-center justify-center flex flex-col font-bold text-xs animate-pulse text-squawk-blue">
              {appEnv === "production" ? "" : "DEV"}
            </div>

            {/* Countdown Component */}
            {isInternalUser && authenticated && (
              <div className="h-20 mx-auto px-1 text-center items-center justify-center flex flex-col ">
                <Sentry.ErrorBoundary
                  fallback={
                    <p className="text-red-500 text-xs">Calendar error</p>
                  }
                >
                  <EconomicCalendarCountdown onClick={(e) => e} />
                </Sentry.ErrorBoundary>
              </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 flex flex-col overflow-y-auto px-2 pb-4 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent hover:scrollbar-thumb-gray-600">
              <div className="mt-4 flex-1">
                <ul role="list" className="-mx-2 space-y-0 text-center">
                  <li key="SquawkPlayer">
                    <Sentry.ErrorBoundary
                      fallback={
                        <p className="text-red-400 text-xs">
                          Squawk Error. Please reload.
                        </p>
                      }
                    >
                      <SquawkPlayerButton />
                    </Sentry.ErrorBoundary>
                  </li>
                  {filteredNavigation.map((item) => (
                    <li key={item.name}>
                      <NavLink
                        to={item.href}
                        className={({ isActive }) =>
                          isActive
                            ? "text-squawk-blue bg-gray-800/50 hover:bg-gray-800 group flex flex-col items-center justify-center rounded-md p-3 text-1.5xs leading-2 font-semibold relative before:absolute before:left-0 before:top-1/4 before:bottom-1/4 before:w-0.5 before:bg-squawk-blue before:rounded-r transition-colors duration-150"
                            : "text-gray-400 hover:text-white hover:bg-gray-800 group flex flex-col items-center justify-center rounded-md p-3 text-1.5xs font-normal leading-2 transition-colors duration-150"
                        }
                      >
                        {!isHeightConstrained && (
                          <item.icon
                            className="h-4 w-4 shrink-0 mb-1"
                            aria-hidden="true"
                          />
                        )}
                        <div>{item.name}</div>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Lower Navigation */}
              <div className="mt-auto pt-3 border-t border-gray-800/50">
                {authenticated ? (
                  <>
                    {userName && (
                      <div className="text-center mb-2 px-2 py-2 text-1.5xs text-gray-300 font-medium break-words select-none">
                        {toCapitalCase(userName)}
                      </div>
                    )}
                    <ul className="-mx-2 space-y-0 text-center">
                      {navigationLower.map((item) => (
                        <li key={item.name}>
                          <NavLink
                            to={item.href}
                            className={({ isActive }) =>
                              isActive
                                ? "text-squawk-blue bg-gray-800/50 hover:bg-gray-800 group flex flex-col items-center justify-center rounded-md p-2 text-1.5xs leading-6 font-normal relative before:absolute before:left-0 before:top-1/4 before:bottom-1/4 before:w-0.5 before:bg-squawk-blue before:rounded-r transition-colors duration-150"
                                : "text-gray-400 hover:text-white hover:bg-gray-800 group flex flex-col items-center justify-center rounded-md p-2 text-1.5xs leading-6 font-normal transition-colors duration-150"
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              gate(() => navigate(item.href))();
                            }}
                          >
                            {!isHeightConstrained && (
                              <item.icon
                                className="h-4 w-4 shrink-0 mb-1"
                                aria-hidden="true"
                              />
                            )}
                            <div>{item.name}</div>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                    <div className="text-gray-400 hover:text-white group flex flex-col items-center justify-center rounded-md  text-1.5xs leading-6 font-semibold -mx-2">
                      <button
                        className="bg-gray-900 hover:bg-gray-800 border-none flex flex-col items-center w-full"
                        onClick={handleLogout}
                      >
                        {!isHeightConstrained && (
                          <ArrowLeftOnRectangleIcon className="mx-auto h-4 w-4 shrink-0 text-gray-400 group-hover:text-white mb-1" />
                        )}
                        <span>Logout</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="-mx-2 text-gray-400 hover:text-white group flex flex-col items-center justify-center rounded-md py-2 text-1.5xs leading-6 font-semibold">
                    <button
                      className="bg-gray-900 hover:bg-gray-800 border-none flex flex-col items-center w-full "
                      onClick={handleLogin}
                    >
                      {!isHeightConstrained && (
                        <ArrowRightOnRectangleIcon className="mx-auto h-4 w-4 shrink-0 mb-1" />
                      )}
                      <span>Login</span>
                    </button>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default SideBar;
